// A browser-only, private report from an exported Netlify Form CSV.
// This intentionally never receives or publishes raw email addresses.
export function parseCsv(source) {
  const text = String(source || "").replace(/^\uFEFF/, "");
  const rows = [];
  let row = [], field = "", quoted = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') { field += '"'; i++; }
      else if (ch === '"') quoted = false;
      else field += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ",") { row.push(field); field = ""; }
    else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      row.push(field);
      if (row.some((cell) => cell.trim())) rows.push(row);
      row = []; field = "";
    } else field += ch;
  }
  row.push(field);
  if (row.some((cell) => cell.trim())) rows.push(row);
  return rows;
}

const key = (value) => String(value || "").trim().toLowerCase().replace(/[^a-z0-9]/g, "");
const monthLabel = (value) => {
  if (!/^2027-(0[1-9]|1[0-2])$/.test(value)) return value;
  return new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(value + "-01T00:00:00Z"));
};
const dominant = (values) => {
  const counts = {};
  for (const value of values) counts[value] = (counts[value] || 0) + 1;
  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  return sorted[0] ? { value: sorted[0][0], count: sorted[0][1] } : null;
};

export function summarizeTourVotes(content) {
  const rows = parseCsv(content);
  if (rows.length < 2) throw new Error("This CSV has no vote rows.");
  const headings = rows.shift().map(key);
  const get = (row, name) => row[headings.indexOf(key(name))] || "";
  if (!headings.includes("city") || !headings.includes("preferredmonth"))
    throw new Error("Choose the CSV from the pole-tour-date-vote-2027 form. It must have city and preferred_month columns.");

  const valid = [];
  for (const row of rows) {
    const city = get(row, "city").trim().slice(0, 90);
    const month = get(row, "preferred_month").trim();
    if (!city || !/^2027-(0[1-9]|1[0-2])$/.test(month)) continue;
    valid.push({
      city, month, weekend: get(row, "preferred_weekend").trim(),
      day: get(row, "day_preference").trim(),
      intent: get(row, "attendance_intent").trim(),
      optIn: /^(yes|true|1|on)$/i.test(get(row, "email_opt_in").trim()) && Boolean(get(row, "email").trim())
    });
  }
  const grouped = new Map();
  for (const vote of valid) {
    const lookup = vote.city.toLocaleLowerCase();
    if (!grouped.has(lookup)) grouped.set(lookup, { city: vote.city, votes: 0, months: [], weekends: [], timeSlots: [], likely: 0, earlyAccess: 0 });
    const entry = grouped.get(lookup);
    entry.votes++;
    entry.months.push(vote.month);
    if (vote.weekend && vote.weekend !== "Any weekend") entry.weekends.push(vote.weekend);
    if (vote.day) entry.timeSlots.push(vote.day);
    if (vote.intent === "Likely to attend") entry.likely++;
    if (vote.optIn) entry.earlyAccess++;
  }
  const cities = [...grouped.values()].map((entry) => ({
    city: entry.city, votes: entry.votes, likely: entry.likely, earlyAccess: entry.earlyAccess,
    topMonth: dominant(entry.months), topWeekend: dominant(entry.weekends), topTime: dominant(entry.timeSlots)
  })).sort((a, b) => b.votes - a.votes || a.city.localeCompare(b.city));
  const months = [...new Set(valid.map((vote) => vote.month))].sort().map((month) => ({
    month, label: monthLabel(month), votes: valid.filter((vote) => vote.month === month).length
  }));
  return { total: valid.length, omitted: rows.length - valid.length, earlyAccess: valid.filter((vote) => vote.optIn).length, cities, months };
}
export { monthLabel };
